import { Subscription } from 'rxjs';
import { inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  path: string;
  routerLink: string[] | undefined;
  key: string;
  lastItem: boolean;
  klasses?: string[];
}

export interface BreadcrumbConfig {
  resource?: string; // Resource Code of the segment
  pageCode?: string; // Page Code of the segment
  label?: string; // Label for the segment - takes precedence to resource/page
  prependRoute?: string; // Virtual route to add before this segment
  prependPageCode?: string;
  prependLabel?: string;
  hide?: boolean; // If true, segment is completely hidden
  hideLink?: boolean; // If true, segment does not include a link
}

/**
 * Base controller for a breadcrumb and toolbar.
 * Use the 'ResourceBreadcrumbToolbarController' if you are managing resources and are using the VocabService.
 */
export class BreadcrumbToolbarController {
  protected breadcrumbs$: Subscription | undefined; // Observable<MenuItem []>;
  protected showCrumbs = signal(true);
  protected breadcrumbs = signal<BreadcrumbItem[]>([]);
  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  protected homeLink = '/';
  protected hideHomeLink = false;

  constructor() {
    this.breadcrumbs$ = this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged(),
      )
      .subscribe((routeEvent) => {
        this.updateCrumbsAndToolbar();
      });
  }

  /**
   * Call from your ngOnInit as well as when routing is complete
   * @protected
   */
  protected updateCrumbsAndToolbar() {
    const currentRoute = this.route.root;
    const crumbs = this.buildBreadcrumbs(currentRoute);
    if (crumbs) {
      this.breadcrumbs.set(crumbs);
    }
    this.updateToolbar(currentRoute);
  }

  /**
   * Builds and array of Breadcrumbs
   */
  protected buildBreadcrumbs(route: ActivatedRoute): BreadcrumbItem[] {
    console.debug('Building Breadcrumbs');

    const breadCrumbs: BreadcrumbItem[] = [];

    const routeLinks: string[] = [];
    let currentRoute: ActivatedRoute | null = route;

    // -- Now add all the url components --//
    while (currentRoute) {
      let label: string | undefined;
      let path: string | undefined = '/';
      let crumbRoute: string[] | undefined;
      let hiddenRoute = false;

      if (currentRoute.routeConfig) {
        path = currentRoute.routeConfig.path;
        if (path != null) {
          let crumbConfig: BreadcrumbConfig;

          if (path !== this.homeLink) {
            //-- Check if we have breadcrumb configuration information in the route --//
            const data = currentRoute.routeConfig.data;
            if (data && data['breadcrumb']) {
              crumbConfig = data['breadcrumb'] as BreadcrumbConfig;
              if (crumbConfig.hide) {
                hiddenRoute = true;
              } else {
                label = this.getCrumbLabel(crumbConfig);
              }
            } else if (path === ':id') {
              label = this.getItemLabel(currentRoute);
              if (!label) {
                label = 'Item';
              }
            }

            if (label == null) {
              label = path;
            }
          }
        }
      } else {
        if (!this.hideHomeLink) {
          label = 'Home';
          path = this.homeLink;
        }
      }

      //-- Push the breadcrumb, as long as we have a label... --//
      if (path) {
        routeLinks.push(path);
        if (!crumbRoute) {
          crumbRoute = [...routeLinks];
        }
        if (label && !hiddenRoute) {
          const breadcrumb: BreadcrumbItem = {
            label: label,
            path: path,
            routerLink: crumbRoute,
            key: crumbRoute.join('/'),
            klasses: ['font-semibold'],
            lastItem: false,
          };

          breadCrumbs.push(breadcrumb);
        }
      }

      currentRoute = currentRoute.firstChild;
    }

    if (breadCrumbs.length > 0) {
      const lastItem = breadCrumbs[breadCrumbs.length - 1];
      lastItem.lastItem = true;
      lastItem.klasses = undefined;
      if (breadCrumbs.length > 1) {
        lastItem.routerLink = undefined;
      }
    }
    return breadCrumbs;
  }

  /**
   *
   * @param crumbConfig
   * @protected
   */
  protected getCrumbLabel(crumbConfig: BreadcrumbConfig): string | undefined {
    let label: string | undefined;
    if (crumbConfig.label) {
      label = crumbConfig.label;
    }
    return label;
  }

  protected getItemLabel(currentRoute: ActivatedRoute): string | undefined {
    //-- If we are an 'id' segment, then grab the real data --//
    const sdata = currentRoute.snapshot.data;
    let label: string | undefined;
    if (sdata) {
      let item;
      //-- First check if we have an item defined, if not, check for a resourceHandler --//
      if (sdata['item']) {
        if (sdata['item'].snapshot) {
          item = sdata['item'].snapshot;
        } else {
          item = sdata['item'];
        }
      } else if (sdata['resourceHandler']) {
        item = sdata['resourceHandler'].item;
      }
      //-- If we have found an item reference, then let's try to pull the breadcrumb name from it --//
      if (item) {
        const path = item.id ?? item.uid;
        if (item.breadcrumbName) {
          label = item.breadcrumbName();
        } else if (item.name) {
          label = item.name;
        } else {
          label = path;
        }
      }
    }
    return label;
  }

  /**
   * Override in your class if you need to set up a toolbar
   * @param route
   * @protected
   */
  protected updateToolbar(route: ActivatedRoute): boolean {
    return false;
  }
}
